/**
 * La barra delle tab in cima a un modulo.
 *
 * Nasce il 27-08-2026 da un'osservazione di Enrico: nel modulo Direzione le
 * sezioni si cambiano con dei pulsanti in cima, negli altri no — in Commesse e
 * Pianificazione le sezioni esistevano **solo** nella barra laterale, e in
 * Spedizioni le tab c'erano ma di un altro stile e solo tre su cinque. Tre
 * grammatiche diverse nello stesso portale.
 *
 * **Le tab nascono dalle stesse sezioni della barra** (`SEZIONI_M1`, `SEZIONI_M2`,
 * …): un modulo non può più avere una lista di sezioni nella barra e un'altra
 * nelle tab, che è esattamente il modo in cui Spedizioni era andata alla deriva.
 * Aggiungere una sezione = una riga in `nav-config.ts`, e compare in entrambi i
 * posti col suo permesso già attaccato.
 *
 * Si naviga per **path**, non per `?tab=`: così una sezione che è una rotta a sé
 * (il Calendario di Marina su `/calendario`, le Partenze di Enzo su `/partenze`)
 * sta nella stessa fila delle altre senza essere un caso speciale, e gli
 * indirizzi corti restano indirizzi veri.
 */
import { Settings } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { useMyPermessi } from "@/hooks/usePermessi";
import type { NavItem } from "@/lib/nav-config";
import { hasPermessoSezione } from "@/lib/permessi";
import { cn } from "@/lib/utils";

/** `/moduli/spedizioni?tab=documenti` → `{ base, tab }`. */
function scomponi(path: string): { base: string; tab: string | null } {
  const [base, query = ""] = path.split("?");
  return { base, tab: new URLSearchParams(query).get("tab") };
}

export function TabModulo({
  sezioni,
  modulo,
  ingranaggioVisibile = false,
  onIngranaggio,
}: {
  /** Le sezioni del modulo, da `nav-config.ts`. */
  sezioni: NavItem[];
  /** Codice del modulo (`"M1"`), da cui le sezioni ereditano il permesso. */
  modulo: string;
  /**
   * Se mostrare l'ingranaggio. Ogni modulo ha la sua regola (M3 lo lega a
   * un'azione, M2 al permesso della sezione), quindi la decisione arriva da
   * fuori: qui c'è solo il posto dove sta.
   */
  ingranaggioVisibile?: boolean;
  onIngranaggio?: () => void;
}) {
  const { data: perms } = useMyPermessi();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const tabCorrente = new URLSearchParams(search).get("tab");

  const visibili = sezioni.filter(
    (s) => !s.ingranaggio && hasPermessoSezione(perms, s.key, modulo),
  );

  // Un modulo con una sezione sola non ha niente da cambiare: la fila di una
  // pillola sarebbe rumore. (Succede a chi ha un solo permesso di sezione.)
  if (visibili.length < 2 && !ingranaggioVisibile) return null;

  /** La sezione attiva: la stessa base **e** lo stesso `?tab=`.
   *
   *  Il confronto sul tab non è un dettaglio: le tre sezioni di Spedizioni che
   *  vivono nella stessa pagina si distinguono solo per quel parametro, e senza
   *  guardarlo si sarebbero accese tutte insieme. */
  function attiva(s: NavItem): boolean {
    const { base, tab } = scomponi(s.path ?? "");
    if (base !== pathname) return false;
    return (tab ?? null) === (tabCorrente ?? null);
  }

  return (
    <nav className="mb-4 flex flex-wrap items-center gap-1 border-b pb-2">
      {visibili.map((s) => {
        const on = attiva(s);
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => s.path && navigate(s.path)}
            aria-current={on ? "page" : undefined}
            className={cn(
              "rounded-md px-3 py-1.5 text-[12.5px] transition-colors",
              on
                ? "bg-primary font-semibold text-primary-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {s.label}
            {s.badge && (
              <span className="ml-1.5 text-[9px] uppercase opacity-70">{s.badge}</span>
            )}
          </button>
        );
      })}

      {ingranaggioVisibile && (
        <button
          type="button"
          onClick={onIngranaggio}
          title="Impostazioni del modulo"
          aria-label="Impostazioni del modulo"
          className="ml-auto rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Settings className="size-4" />
        </button>
      )}
    </nav>
  );
}
